import React, { useState, useEffect } from 'react';
import { Input, Label } from 'reactstrap';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  getAllPayments,
  getExternalPayment,
  getRefundList,
  getSubcriptionCourse,
  refundRequest as submitRefundRequest,
} from '../../../../Api/apiUtils'; // Import API utilities
import { MixpanelEvents,trackEvent } from '../../../../utils/mixpanelTracking';
import {
  Card,
  Button,
  Badge,
  Table,
  Field,
  TextInput,
  Select,
  DescriptionList,
  Loader,
  EmptyState,
} from '../../../../ui';
import { cx } from '../../../../ui/components/cx';
import { StudentStatusBadge, FormGrid } from './ui-pending';
import './style.scss';
import './panels.scss';

const isInstrumentComboPayment = (payment: any) => {
  return payment?.instrumentCombo === true || payment?.instrumentCombo?.enabled === true;
};

const getInstrumentDiscountedPrice = (payment: any) => {
  return +(
    payment?.instrumentDiscountedPrice
    || payment?.razorpay_response?.instrumentDiscountedPrice
    || 0
  );
};

const getTransactionCourseDisplayName = (transaction: any, course: any) => {
  const baseName = course.courseName;
  return isInstrumentComboPayment(transaction) ? `${baseName} - Combo` : baseName;
};

const getTransactionCourseTotalPrice = (transaction: any, course: any, courseIndex: number) => {
  const courseTotal = (course.classesToPurchase * course.unit_price) - +(course.discountedPrice || 0);
  const baseAmount = +(course.finalAmount || course.totalFinalPrice || 0) || courseTotal;
  if (isInstrumentComboPayment(transaction) && courseIndex === 0) {
    return baseAmount + getInstrumentDiscountedPrice(transaction);
  }
  return baseAmount;
};

interface TransactionListProps {
  studentId: string;
}

const Refund: React.FC<TransactionListProps> = ({ studentId }) => {
  const [subscriptionCourse, setSubscriptionCourse] = useState<any[]>([]);
  const [transactionList, setTransactionList] = useState<any[]>(() => {
    return [];
  });
  const [refundRequestList, setRefundRequestList] = useState<any[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchParam] = useSearchParams();

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      const [payments, externalPayments, refundRequests, subscriptionCourses] = await Promise.all([
        getAllPayments(studentId),
        getExternalPayment(studentId),
        getRefundList(studentId),
        getSubcriptionCourse(studentId),
      ]);
  
      const refundRequestIds = refundRequests.data?.Items?.map((v: any) => v.id) || [];
  
      setSubscriptionCourse(subscriptionCourses.data || []);
      
      // Process transactions with default today's date
      const processedTransactions = [
        ...(payments?.filter((e: any) =>
          (e.payment_status?.includes('payment_success') || e.webhook_init) &&
          e.courseList &&
          e.transactionStatus !== 'COURSE_CHANGED' &&
          !refundRequestIds.includes(e.id)
        ) || []),
        ...(externalPayments.filter((e: any) =>
          (!e.isFreeClass || !e.isFree) &&
          e.transactionStatus !== 'COURSE_CHANGED' &&
          !refundRequestIds.includes(e.id)
        ) || []),
      ].map(transaction => {
        let otherDiscountAmount = 0;
          if (transaction.myCash || transaction.artiumCash || transaction.referralCode) {
            otherDiscountAmount += (+transaction.artiumCash + +transaction.myCash);
          }
          if (transaction.specialDiscount || transaction.additionalDiscount || transaction.flashDiscount) {
            otherDiscountAmount += (+transaction.specialDiscount || 0) + (+transaction.additionalDiscount || 0) + (+transaction.flashDiscount || 0);
          }

          if (otherDiscountAmount) {
            const totalPriceWithoutDiscount = transaction.courseList
              .filter((e:any) => !e.isFree)
              .reduce((acc:any, course:any) => acc + (+course.unit_price * +course.classesToPurchase), 0);
            const otherDiscountPercentage = (otherDiscountAmount / (totalPriceWithoutDiscount)) * 100;
            transaction.courseList.filter((e:any) => !e.isFree).forEach((course:any) => {
              course.discountedPrice = +course.discountedPrice || 0;
              course.discountedPrice += (+(+course.unit_price * +course.classesToPurchase) * otherDiscountPercentage / 100);
            });
          }

        return {
          ...transaction,
          captureDate: transaction.captureDate || new Date().toISOString().split('T')[0]
        };
      });
  
      // Calculate refund data for all transactions immediately
      processedTransactions.forEach(transaction => {
        dayCalculation(transaction, subscriptionCourses.data || []);
      });
  
      setTransactionList(processedTransactions);
      setRefundRequestList(refundRequests.data?.Items || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr:any) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-based
  
    const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
    const weekday = weekdayNames[date.getDay()];
    const monthName = monthNames[date.getMonth()];
  
    return `${weekday}, ${String(day).padStart(2, '0')} ${monthName} ${year}`;
  };
  
  const handleChange = (index: number, idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (isInstrumentComboPayment(transactionList[index])) return;
    const { name, value } = e.target;
    const updatedTransactions = JSON.parse(JSON.stringify(transactionList));
    updatedTransactions[index].courseList[idx] = {
      ...updatedTransactions[index].courseList[idx],
      [name]: value,
    };
    let discountPerClass =  +updatedTransactions[index].courseList[idx].discountedPrice ? +updatedTransactions[index].courseList[idx].discountedPrice / +updatedTransactions[index].courseList[idx].classesToPurchase : 0;
    if(name != "refundDiscout"){
      updatedTransactions[index].courseList[idx].refundDiscout = (discountPerClass * updatedTransactions[index].courseList[idx].requestedRefundClasses) || 0;
    }
    updatedTransactions[index].courseList[idx].refundableAmount =
      (updatedTransactions[index].courseList[idx].requestedRefundClasses || 0) *
      updatedTransactions[index].courseList[idx].unit_price -
      (updatedTransactions[index].courseList[idx].refundDiscout || 0);
    setTransactionList(updatedTransactions);
     // If this is the currently selected transaction, update it too
     if (selectedTransaction && selectedTransaction.id === updatedTransactions[index].id) {
      setSelectedTransaction(updatedTransactions[index]);
    }
  };

  const handleReasonChange = (index: number, e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    const updatedTransactions = [...transactionList];
    (updatedTransactions[index] as Record<string, any>)[name] = value;
    setTransactionList(updatedTransactions);

     // If this is the currently selected transaction, update it too
     if (selectedTransaction && selectedTransaction.id === updatedTransactions[index].id) {
      setSelectedTransaction(updatedTransactions[index]);
    }
  };
  const handleDateChange = (index: number, value: string) => {
    const updatedTransactions = [...transactionList];
    updatedTransactions[index].captureDate = value.trim();
    dayCalculation(updatedTransactions[index], subscriptionCourse);
    
    setTransactionList(updatedTransactions);
    
    // If this is the currently selected transaction, update it too
    if (selectedTransaction && selectedTransaction.id === updatedTransactions[index].id) {
      setSelectedTransaction(updatedTransactions[index]);
    }
  };
  
  const handleRefundRequest = async () => {
    if(selectedTransaction && selectedTransaction.courseList.filter((e:any) => e.requestedRefundClasses).length){
      if (!selectedTransaction || !selectedTransaction.reasonforrefund?.trim()) {
        toast.error('Please enter a reason for refund.');
        return;
      }
  
      setLoading(true);
      delete selectedTransaction.razorpay_response;
      delete selectedTransaction.webhook_response;
      try {
        const payload = {
          ...selectedTransaction,
          refund_status: 'pending',
          active: true,
          courseList: selectedTransaction.courseList.map((course:any) => ({
            ...course,
            approvel_classes: course.requestedRefundClasses,
            approvel_amount: course.refundableAmount,
            approvel_discount: course.refundDiscout,
          })),
        };
  
        await submitRefundRequest(payload);
        toast.success('Refund request submitted successfully.');

        trackEvent(MixpanelEvents.PAYMENT_PACKAGE, {
          studentId:studentId,
          studentName:selectedTransaction.name,
       courseName:selectedTransaction.courseList.map((course:any)=>course.courseName).join(","),
       courseId:selectedTransaction.courseList.map((course:any)=>course.courseId).join(","),
       selectedReason:selectedTransaction.reasonforrefund,
       transactionDate:selectedTransaction.captureDate,
          type: "Trasaction List Request Refund",
        });
        init();

      } catch (error) {
        toast.error('Failed to submit refund request.');
      } finally {
        setLoading(false);
      }

    }
  };

  const dayCalculation = (transaction: any, subscription: any[]) => {
    const startDate = new Date(transaction.invoice_date);
    const endDate = new Date(transaction.captureDate);
    const cutoffDate = new Date('2025-04-01');

    // Total days in whole range
    const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    let expiredClassCount = 0;

    if (totalDays <= 0) {
      expiredClassCount = 0;
    } else if (startDate > cutoffDate) {
      // If invoice_date is after cutoff, use only 6-day blocks
      expiredClassCount = Math.floor(totalDays / 6);
    }
     else {
      // Days before and after cutoff
      const beforeCutoffDays = Math.max(0, Math.floor((cutoffDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
      const afterCutoffDays = Math.max(0, Math.floor((endDate.getTime() - cutoffDate.getTime()) / (1000 * 60 * 60 * 24)));

      // Step 1: Use 7-day blocks using before + borrowed from after
      let full7DayClasses = Math.floor(beforeCutoffDays / 7);
      let remainderBefore = beforeCutoffDays % 7;

      let total7BlockCount = full7DayClasses;
      let borrowedFromAfter = 0;

      // Try to form 7-day block from remainderBefore + afterCutoffDays
      if (remainderBefore > 0 && (remainderBefore + afterCutoffDays) >= 7) {
        total7BlockCount += 1;
        borrowedFromAfter = 7 - remainderBefore;
      }

      // Step 2: Use remaining afterCutoffDays for 6-day blocks
      const remainingAfter = afterCutoffDays - borrowedFromAfter;
      const full6DayClasses = Math.floor(remainingAfter / 6);

      expiredClassCount = total7BlockCount + full6DayClasses;
    }

    transaction.courseList.forEach((course:any) => {
      if (isInstrumentComboPayment(transaction)) {
        course.eligibleRefundClasses = 0;
        course.requestedRefundClasses = 0;
        course.refundDiscout = 0;
        course.refundableAmount = 0;
        return;
      }

      if (!course.isFree) {
        let discountPerClass =  +course.discountedPrice ? +course.discountedPrice / +course.classesToPurchase : 0;
        const completedClass = subscription.find((c) => c.courseId === course.courseId)?.classesAvailableForRefund || 0;
        let refundClasses;
          if(expiredClassCount > completedClass){
            refundClasses = (+course.classesToPurchase < expiredClassCount) ? 0 : +course.classesToPurchase - expiredClassCount;
            expiredClassCount -= (+course.classesToPurchase - refundClasses);
          }else{
            refundClasses = +course.classesToPurchase - completedClass;
            expiredClassCount = 0;
          }
        //let refundClasses = Math.max(0, course.classesToPurchase - expiredClassCount);
        course.eligibleRefundClasses = refundClasses > 0 ? refundClasses : 0;
        course.requestedRefundClasses = course.eligibleRefundClasses;
        course.refundDiscout = +(discountPerClass * course.requestedRefundClasses) || 0;
        course.refundableAmount = (course.requestedRefundClasses * course.unit_price) - +course.refundDiscout || 0;
      } else {
        course.eligibleRefundClasses = 0;
        course.requestedRefundClasses = course.eligibleRefundClasses;
        course.refundableAmount = 0;
      }
    });
  };

  const discountPct = (course: any) =>
    course.discountedPrice
      ? `${Math.round((+course.discountedPrice / (course.classesToPurchase * course.unit_price)) * 100)}%`
      : "0%";

  const reasonOptions = [
    { value: "", label: "Select a reason" },
    ...[
      "Personal reasons",
      "Academics Issues",
      "Teacher Escalations",
      "Previous teacher unavailable",
      "Prefers Offline",
      "Medical emergency",
      "Child is too small to cope up",
      "Operational Issue",
      "Desired slots unavailable",
      "Technical Issues",
      "Moved to Artium offline",
    ].map((r) => ({ value: r, label: r })),
  ];

  return (
    <div className="transaction-list-page ds-stack">
      {loading ? (
        <Loader label="Loading transactions…" />
      ) : (
        <>
          <h3 className="ds-card__title">Transaction list</h3>
          {transactionList.length === 0 && (
            <EmptyState
              title="No refundable transactions"
              description="Only successful payments without an open refund request are listed here."
            />
          )}
          {transactionList.map((transaction, index) => (
            <Card
              key={transaction.id}
              className={cx(
                "transaction-list-card",
                transaction.id === selectedTransaction?.id && "transaction-list-card--selected"
              )}
            >
              <div className="transaction-list-card-header">
                <Input
                  type="radio"
                  id={`txn-select-${transaction.id}`}
                  checked={transaction.id === selectedTransaction?.id}
                  disabled={isInstrumentComboPayment(transaction)}
                  onChange={() => {
                    if (isInstrumentComboPayment(transaction)) return;
                    setSelectedTransaction(transaction);
                  }}
                />
                <Label check for={`txn-select-${transaction.id}`}>
                  Select for refund
                  <span className="ds-text-subtle d-md-none"> · {formatDate(transaction.invoice_date || "")}</span>
                </Label>
              </div>

              {/* Desktop / tablet: scrollable table */}
              <div className="d-none d-md-block">
                <Table
                  className="transaction-list-table"
                  caption={`Courses in the transaction of ${formatDate(transaction.invoice_date || "")}`}
                >
                  <thead>
                    <tr>
                      <th>Transaction Date</th>
                      <th>Course Name</th>
                      <th className="ds-num">Price/Class ({transaction.currency})</th>
                      <th className="ds-num">Classes</th>
                      <th className="ds-num">Discount (%)</th>
                      <th className="ds-num">Total Price ({transaction.currency})</th>
                      <th className="ds-num">Refundable Classes</th>
                      <th className="ds-num">Refundable Amount ({transaction.currency})</th>
                      <th className="ds-num">Discount on Refund ({transaction.currency})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transaction.courseList.map((course: any, idx: number) => (
                      <tr key={idx}>
                        <td>{idx === 0 ? formatDate(transaction.invoice_date || "") : ""}</td>
                        <td>
                          {getTransactionCourseDisplayName(transaction, course)}
                          {course.isFree && <> <Badge tone="success">Free course</Badge></>}
                        </td>
                        <td className="ds-num">{course.unit_price}</td>
                        <td className="ds-num">{course.classesToPurchase}</td>
                        <td className="ds-num">{discountPct(course)}</td>
                        <td className="ds-num">{getTransactionCourseTotalPrice(transaction, course, idx)}</td>
                        <td>
                          <TextInput
                            size="sm"
                            numeric
                            type="number"
                            name="requestedRefundClasses"
                            aria-label={`Refundable classes for ${course.courseName}`}
                            value={
                              isInstrumentComboPayment(transaction) ? 0 : course.requestedRefundClasses
                            }
                            onChange={(e) => handleChange(index, idx, e)}
                            disabled={
                              course.isFree ||
                              isInstrumentComboPayment(transaction) ||
                              !(transaction.id == selectedTransaction?.id)
                            }
                          />
                        </td>
                        <td>
                          <TextInput
                            size="sm"
                            numeric
                            type="number"
                            name="refundableAmount"
                            aria-label={`Refundable amount for ${course.courseName}`}
                            value={
                              isInstrumentComboPayment(transaction)
                                ? 0
                                : +(Number(course.refundableAmount).toFixed(2))
                            }
                            disabled
                          />
                        </td>
                        <td>
                          <TextInput
                            size="sm"
                            numeric
                            type="number"
                            name="refundDiscout"
                            aria-label={`Discount on refund for ${course.courseName}`}
                            value={
                              isInstrumentComboPayment(transaction)
                                ? 0
                                : +(Number(course.refundDiscout).toFixed(2))
                            }
                            onChange={(e) => handleChange(index, idx, e)}
                            disabled={
                              course.isFree ||
                              isInstrumentComboPayment(transaction) ||
                              !(transaction.id == selectedTransaction?.id)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile: stacked cards */}
              <div className="d-md-none transaction-list-mobile">
                {transaction.courseList.map((course: any, idx: number) => {
                  const combo = isInstrumentComboPayment(transaction);
                  const selected = transaction.id === selectedTransaction?.id;
                  const disabled = course.isFree || combo || !selected;
                  return (
                    <div key={idx} className="transaction-list-mobile-item">
                      <div className="txn-mobile-row">
                        <span className="txn-mobile-label">Course</span>
                        <span className="txn-mobile-value">
                          {getTransactionCourseDisplayName(transaction, course)}
                          {course.isFree && <> <Badge tone="success">Free</Badge></>}
                        </span>
                      </div>
                      <div className="txn-mobile-grid">
                        <div>
                          <span className="txn-mobile-label">Price/Class ({transaction.currency})</span>
                          <span className="txn-mobile-value">{course.unit_price}</span>
                        </div>
                        <div>
                          <span className="txn-mobile-label">Classes</span>
                          <span className="txn-mobile-value">{course.classesToPurchase}</span>
                        </div>
                        <div>
                          <span className="txn-mobile-label">Discount</span>
                          <span className="txn-mobile-value">{discountPct(course)}</span>
                        </div>
                        <div>
                          <span className="txn-mobile-label">Total ({transaction.currency})</span>
                          <span className="txn-mobile-value">
                            {getTransactionCourseTotalPrice(transaction, course, idx)}
                          </span>
                        </div>
                      </div>
                      <div className="ds-stack">
                        <Field label="Refundable classes">
                          {(ctl) => (
                            <TextInput
                              {...ctl}
                              numeric
                              type="number"
                              name="requestedRefundClasses"
                              value={combo ? 0 : course.requestedRefundClasses}
                              onChange={(e) => handleChange(index, idx, e)}
                              disabled={disabled}
                            />
                          )}
                        </Field>
                        <Field label={`Refundable amount (${transaction.currency})`}>
                          {(ctl) => (
                            <TextInput
                              {...ctl}
                              numeric
                              type="number"
                              name="refundableAmount"
                              value={combo ? 0 : +(Number(course.refundableAmount).toFixed(2))}
                              disabled
                            />
                          )}
                        </Field>
                        <Field label={`Discount on refund (${transaction.currency})`}>
                          {(ctl) => (
                            <TextInput
                              {...ctl}
                              numeric
                              type="number"
                              name="refundDiscout"
                              value={combo ? 0 : +(Number(course.refundDiscout).toFixed(2))}
                              onChange={(e) => handleChange(index, idx, e)}
                              disabled={disabled}
                            />
                          )}
                        </Field>
                      </div>
                    </div>
                  );
                })}
              </div>

              {transaction.freeClassReason ? (
                <div className="transaction-list-card-section">
                  <DescriptionList
                    items={[{ label: "Reason for free class", value: transaction.freeClassReason }]}
                  />
                </div>
              ) : null}

              {transaction.id === selectedTransaction?.id && (
                <div className="transaction-list-card-section">
                  <FormGrid min={240}>
                    <Field label="Capture date of refund" required>
                      {(ctl) => (
                        <DatePicker
                          id={ctl.id}
                          selected={
                            selectedTransaction?.captureDate
                              ? new Date(selectedTransaction.captureDate)
                              : new Date()
                          }
                          onChange={(date: Date | null) => {
                            if (selectedTransaction) {
                              const idx = transactionList.findIndex(
                                (t) => t.id === selectedTransaction.id
                              );
                              if (idx !== -1) {
                                handleDateChange(
                                  idx,
                                  date
                                    ? date.toISOString().split("T")[0]
                                    : new Date().toISOString().split("T")[0]
                                );
                              }
                            }
                          }}
                          dateFormat="yyyy-MM-dd"
                          placeholderText="Select date"
                          showPopperArrow={true}
                          isClearable
                          customInput={<TextInput />}
                          popperPlacement="top-start"
                          portalId="refund-date-portal"
                          popperClassName="refund-datepicker-popper"
                        />
                      )}
                    </Field>
                    <Field label="Reason for refund" required>
                      {(ctl) => (
                        <Select
                          {...ctl}
                          name="reasonforrefund"
                          options={reasonOptions}
                          value={transaction.reasonforrefund}
                          onChange={(e) => handleReasonChange(index, e)}
                        />
                      )}
                    </Field>
                    <div className="transaction-list-submit">
                      <Button
                        variant="primary"
                        block
                        onClick={handleRefundRequest}
                        disabled={loading}
                      >
                        {loading ? "Submitting..." : "Request Refund"}
                      </Button>
                    </div>
                  </FormGrid>
                </div>
              )}
            </Card>
          ))}
          {refundRequestList.length > 0 && (
            <>
              <h3 className="ds-card__title">Refund request list</h3>
              {refundRequestList.map((refund) => (
                <Card
                  key={refund.id}
                  className="transaction-list-card"
                  title="Refund request"
                  subtitle={formatDate(refund.invoice_date || "")}
                  actions={<StudentStatusBadge status={refund.refund_status} />}
                >
                  <div className="d-none d-md-block">
                    <Table
                      className="transaction-list-table"
                      caption={`Courses in the refund request of ${formatDate(refund.invoice_date || "")}`}
                    >
                      <thead>
                        <tr>
                          <th>Transaction Date</th>
                          <th>Course Name</th>
                          <th className="ds-num">Price/Class</th>
                          <th className="ds-num">Classes</th>
                          <th className="ds-num">Discount (%)</th>
                          <th className="ds-num">Total Price</th>
                          <th className="ds-num">Refundable Classes</th>
                          <th className="ds-num">Refundable Amount</th>
                          <th className="ds-num">Discount on Refund</th>
                        </tr>
                      </thead>
                      <tbody>
                        {refund.courseList.map((course: any, idx: number) => (
                          <tr key={idx}>
                            <td>{idx === 0 ? formatDate(refund.invoice_date || "") : ""}</td>
                            <td>
                              {getTransactionCourseDisplayName(refund, course)}
                              {course.isFree && <> <Badge tone="success">Free course</Badge></>}
                            </td>
                            <td className="ds-num">{course.unit_price}</td>
                            <td className="ds-num">{course.classesToPurchase}</td>
                            <td className="ds-num">{discountPct(course)}</td>
                            <td className="ds-num">{getTransactionCourseTotalPrice(refund, course, idx) || course.price}</td>
                            <td className="ds-num">{course.approvel_classes}</td>
                            <td className="ds-num">{course.approvel_amount}</td>
                            <td className="ds-num">{course.approvel_discount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-md-none transaction-list-mobile">
                    {refund.courseList.map((course: any, idx: number) => (
                      <div key={idx} className="transaction-list-mobile-item">
                        {idx === 0 && (
                          <div className="txn-mobile-row">
                            <span className="txn-mobile-label">Transaction Date</span>
                            <span className="txn-mobile-value">
                              {formatDate(refund.invoice_date || "")}
                            </span>
                          </div>
                        )}
                        <div className="txn-mobile-row">
                          <span className="txn-mobile-label">Course</span>
                          <span className="txn-mobile-value">
                            {getTransactionCourseDisplayName(refund, course)}
                            {course.isFree && <> <Badge tone="success">Free</Badge></>}
                          </span>
                        </div>
                        <div className="txn-mobile-grid">
                          <div>
                            <span className="txn-mobile-label">Price/Class</span>
                            <span className="txn-mobile-value">{course.unit_price}</span>
                          </div>
                          <div>
                            <span className="txn-mobile-label">Classes</span>
                            <span className="txn-mobile-value">{course.classesToPurchase}</span>
                          </div>
                          <div>
                            <span className="txn-mobile-label">Discount</span>
                            <span className="txn-mobile-value">{discountPct(course)}</span>
                          </div>
                          <div>
                            <span className="txn-mobile-label">Total</span>
                            <span className="txn-mobile-value">
                              {getTransactionCourseTotalPrice(refund, course, idx) || course.price}
                            </span>
                          </div>
                          <div>
                            <span className="txn-mobile-label">Refundable Classes</span>
                            <span className="txn-mobile-value">{course.approvel_classes}</span>
                          </div>
                          <div>
                            <span className="txn-mobile-label">Refundable Amount</span>
                            <span className="txn-mobile-value">{course.approvel_amount}</span>
                          </div>
                          <div>
                            <span className="txn-mobile-label">Discount on Refund</span>
                            <span className="txn-mobile-value">{course.approvel_discount}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="transaction-list-card-section">
                    <DescriptionList
                      items={[{ label: "Reason for refund", value: refund.reasonforrefund }]}
                    />
                  </div>
                </Card>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Refund;